import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, Textarea } from '@/components/ui';
import { Label } from 'components/ui/label';
import type { GenerationFieldOption } from 'modules/generationFields';

interface FieldPromptInputProps {
  fields: GenerationFieldOption[];
  fieldKey: string;
  fieldPrompt: string;
  onFieldKeyChange: (key: string) => void;
  onFieldPromptChange: (prompt: string) => void;
  /** Override the Field selector label (defaults to the translated "Field (optional)"). */
  fieldLabel?: string;
  className?: string;
}

/** Pre-fill only overwrites text the user hasn't touched, so manual edits survive a field re-selection. */
export const FieldPromptInput: React.FC<FieldPromptInputProps> = ({
  fields,
  fieldKey,
  fieldPrompt,
  onFieldKeyChange,
  onFieldPromptChange,
  fieldLabel,
  className,
}) => {
  const { t } = useTranslation();
  // The prompt text we last auto-filled, so we can tell "untouched" from "edited".
  const lastAutofillRef = useRef('');

  const handleFieldChange = (key: string) => {
    onFieldKeyChange(key);
    const nextPrompt = fields.find((f) => f.key === key)?.prompt ?? '';
    const untouched = fieldPrompt.trim() === '' || fieldPrompt === lastAutofillRef.current;
    if (untouched) {
      onFieldPromptChange(nextPrompt);
      lastAutofillRef.current = nextPrompt;
    }
  };

  return (
    <div className={className}>
      <div>
        <Label className="mb-1 block">{fieldLabel ?? t('field_optional')}</Label>
        <Combobox
          options={fields.map((f) => ({ value: f.key, label: f.displayName }))}
          value={fieldKey}
          onChange={handleFieldChange}
          allowClear
          clearLabel={t('any_field_generic')}
          placeholder={t('any_field_generic')}
          searchPlaceholder={t('search_fields')}
          emptyText={t('no_fields_found')}
        />
      </div>

      <div className="mt-3">
        <Label className="mb-1 block">{t('generation_instructions_optional')}</Label>
        <Textarea
          value={fieldPrompt}
          onChange={(e) => onFieldPromptChange(e.target.value)}
          placeholder={t('generation_instructions_placeholder')}
          rows={4}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          {t('generation_instructions_hint')}
        </p>
      </div>
    </div>
  );
};

export default FieldPromptInput;
